(() => {
  if (customElements.get('hs-cart-drawer')) return;
  const nativeFetch = window.fetch;
  const announceMutation = (url) => {
    try {
      const target = new URL(url, location.origin);
      if (target.origin === location.origin && /\/cart\/(add|change|update|clear)(\.js)?$/.test(target.pathname)) {
        document.dispatchEvent(new CustomEvent('hs:cart-mutated', { detail: { added: /\/add/.test(target.pathname) } }));
      }
    } catch (_) {}
  };
  window.fetch = function (...args) {
    return nativeFetch.apply(this, args).then(response => {
      if (response.ok) announceMutation(typeof args[0] === 'string' || args[0] instanceof URL ? String(args[0]) : args[0].url);
      return response;
    });
  };
  const nativeOpen = XMLHttpRequest.prototype.open;
  const nativeSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url, ...args) {
    this.hsCartUrl = String(url);
    return nativeOpen.call(this, method, url, ...args);
  };
  XMLHttpRequest.prototype.send = function (...args) {
    this.addEventListener('load', () => {
      if (this.status >= 200 && this.status < 300) announceMutation(this.hsCartUrl);
    }, { once: true });
    return nativeSend.apply(this, args);
  };

  class HSCartDrawer extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.dialog = this.querySelector('dialog');
      this.designMode = this.dataset.designMode === 'true';
      this.root = this.dataset.root || '/';
      this.strings = JSON.parse(this.querySelector('[data-hs-strings]').textContent);
      this.pendingForms = new WeakSet();
      this.queue = Promise.resolve();
      this.timers = [];
      this.codes = [];
      this.lastContent = this.querySelector('[data-hs-content]').innerHTML;
      this.mutationVersion = 0;
      this.abortController = new AbortController();
      const options = { signal: this.abortController.signal };
      const listen = (target, name, handler, capture = false) => target.addEventListener(name, handler, { ...options, capture });
      listen(document, 'submit', event => this.onSubmit(event), true);
      listen(document, 'click', event => this.onClick(event), true);
      const commitQuantity = event => {
        if (event.target.matches('[data-hs-quantity]') && this.contains(event.target)) {
          clearTimeout(this.quantityTimer);
          const quantity = Number(event.target.value);
          if (!Number.isInteger(quantity) || quantity < 0) {
            event.target.value = event.target.defaultValue;
            return;
          }
          this.change(event.target.closest('[data-hs-line]').dataset.hsLine, quantity);
        }
      };
      listen(document, 'change', commitQuantity, true);
      listen(this, 'change', event => {
        if (!event.target.matches('[data-hs-suggestion-variant]')) return;
        this.updateSuggestionPrice(event.target);
      });
      listen(this, 'scroll', event => {
        if (event.target.matches('.hs-cart__carousel')) this.updateCarouselControls();
      }, true);
      this.carouselObserver = new ResizeObserver(() => this.updateCarouselControls());
      this.carouselObserver.observe(this.dialog);
      listen(document, 'input', event => {
        if (event.target.matches('[data-hs-quantity]') && this.contains(event.target)) {
          clearTimeout(this.quantityTimer);
          this.quantityTimer = setTimeout(() => commitQuantity(event), 600);
        }
      }, true);
      listen(this.dialog, 'click', event => {
        if (event.target === this.dialog && event.clientX < this.dialog.getBoundingClientRect().left) this.close();
      });
      listen(this.dialog, 'cancel', event => {
        event.preventDefault();
        this.close();
      });
      listen(this.dialog, 'animationend', event => {
        if (event.target === this.dialog && event.animationName === 'hs-cart-out') this.finishClose();
      });
      listen(this.dialog, 'keydown', event => {
        if (event.key !== 'Tab') return;
        const focusable = [...this.dialog.querySelectorAll('a[href],button:not(:disabled),input:not(:disabled):not([type="hidden"]),select:not(:disabled),[tabindex="0"]')].filter(element => element.getClientRects().length && !element.closest('[hidden]'));
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && (document.activeElement === first || !this.dialog.contains(document.activeElement))) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      });
      listen(this.dialog, 'close', () => {
        if (this.dialog.open) return;
        this.cancelClosing();
        this.dialog.style.removeProperty('--hs-cart-open-from');
        if (this.designMode) { HSCartDrawer.editorOpen = false; HSCartDrawer.editorBlockId = null; }
        document.documentElement.classList.remove('hs-cart-open');
        this.returnFocus?.focus({ preventScroll: true });
      });
      listen(document, 'hs:cart-mutated', event => {
        this.mutationVersion++;
        if (event.detail.added && !this.busy) this.open(false);
        this.scheduleRefresh();
      });
      ['cart:open', 'minicart:open', 'open-cart-drawer'].forEach(name => listen(document, name, () => this.open()));
      ['cart:refresh', 'shopify:cart:lines-update'].forEach(name => listen(document, name, () => this.scheduleRefresh()));
      listen(window, 'pageshow', event => { if (event.persisted || !this.designMode) this.refresh(); });
      if (this.designMode) {
        const selectEditorContent = event => {
          if (event.detail?.sectionId !== this.dataset.sectionId) return;
          this.open(false);
          const id = event.detail.blockId;
          const block = id ? this.querySelector(`[data-hs-block-id="${CSS.escape(id)}"]`) : null;
          this.editorAnnouncement = block?.matches('[data-hs-announcement]') ? id : null;
          HSCartDrawer.editorBlockId = id || null;
          if (this.editorAnnouncement) this.querySelectorAll('[data-hs-announcement]').forEach(message => { message.hidden = message !== block; });
          else block?.scrollIntoView({ block: 'nearest' });
        };
        listen(document, 'shopify:section:select', selectEditorContent);
        listen(document, 'shopify:block:select', selectEditorContent);
        listen(document, 'shopify:block:deselect', event => {
          if (event.detail?.sectionId === this.dataset.sectionId) this.editorAnnouncement = null;
        });
        listen(document, 'shopify:section:deselect', event => {
          if (event.detail?.sectionId === this.dataset.sectionId && this.dataset.editorPreview !== 'true') this.close();
        });
      }
      const subscribe = () => {
        if (window.MinimogEvents?.subscribe) this.unsubscribe = window.MinimogEvents.subscribe('cart-update', () => this.scheduleRefresh());
      };
      if (document.readyState === 'loading') listen(document, 'DOMContentLoaded', subscribe);
      else subscribe();
      this.announcementTimer = setInterval(() => {
        if (!this.dialog.open || document.hidden || this.editorAnnouncement || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        const messages = [...this.querySelectorAll('[data-hs-announcement]')];
        if (messages.length < 2) return;
        const index = messages.findIndex(message => !message.hidden);
        messages.forEach((message, i) => { message.hidden = i !== (index + 1) % messages.length; });
      }, Number(this.dataset.announcementInterval) || 5000);
      window.HSCartDrawer = this;
      this.syncCounts();
      this.updateCarouselControls();
      if (this.designMode) {
        this.editorBaseline = this.querySelector('[data-hs-content]').cloneNode(true);
        if (this.dataset.editorPreview === 'true' || HSCartDrawer.editorOpen) {
          this.open(false);
          const selected = HSCartDrawer.editorBlockId && this.querySelector(`[data-hs-block-id="${CSS.escape(HSCartDrawer.editorBlockId)}"]`);
          if (selected?.matches('[data-hs-announcement]')) {
            this.editorAnnouncement = HSCartDrawer.editorBlockId;
            this.querySelectorAll('[data-hs-announcement]').forEach(message => { message.hidden = message !== selected; });
          }
        }
      } else this.refresh();
    }

    disconnectedCallback() {
      if (this.designMode) HSCartDrawer.editorOpen = this.dialog.open && !this.closing;
      this.abortController?.abort();
      this.cancelClosing();
      this.timers.forEach(clearTimeout);
      clearInterval(this.announcementTimer);
      clearTimeout(this.quantityTimer);
      this.carouselObserver?.disconnect();
      if (typeof this.unsubscribe === 'function') this.unsubscribe();
      document.documentElement.classList.remove('hs-cart-open');
      this.initialized = false;
    }

    open(refresh = true) {
      if (this.closing) {
        this.dialog.style.setProperty('--hs-cart-open-from', getComputedStyle(this.dialog).transform);
        this.cancelClosing();
      }
      if (!this.dialog.open) {
        this.dialog.style.removeProperty('--hs-cart-open-from');
        this.returnFocus = document.activeElement;
        this.dialog.showModal();
        document.documentElement.classList.add('hs-cart-open');
        this.querySelector('[data-hs-close]').focus({ preventScroll: true });
        this.updateCarouselControls();
      }
      if (this.designMode) HSCartDrawer.editorOpen = true;
      if (refresh && !this.designMode) this.refresh();
    }

    close() {
      if (!this.dialog.open || this.closing) return;
      if (this.designMode) { HSCartDrawer.editorOpen = false; HSCartDrawer.editorBlockId = null; }
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.dialog.close();
        return;
      }
      this.dialog.style.setProperty('--hs-cart-close-from', getComputedStyle(this.dialog).transform);
      this.closing = true;
      this.dialog.classList.add('is-closing');
      this.closeTimer = setTimeout(() => this.finishClose(), 280);
    }

    finishClose() {
      if (!this.closing) return;
      this.dialog.close();
      this.cancelClosing();
    }

    cancelClosing() {
      clearTimeout(this.closeTimer);
      this.closing = false;
      this.dialog.classList.remove('is-closing');
      this.dialog.style.removeProperty('--hs-cart-close-from');
    }

    updateSuggestionPrice(select) {
      const option = select.selectedOptions[0];
      if (!option) return;
      const form = select.closest('[data-hs-suggestion]');
      form.querySelector('[data-hs-suggestion-price]').textContent = option.dataset.price;
      const compare = form.querySelector('[data-hs-suggestion-compare]');
      compare.textContent = option.dataset.compare;
      compare.hidden = !option.dataset.compare;
      select.closest('.hs-cart__suggestion').querySelectorAll('[data-hs-suggestion-link]').forEach(link => {
        const url = new URL(link.href, location.origin);
        url.searchParams.set('variant', select.value);
        link.href = url.href;
      });
    }

    updateCarouselControls() {
      const section = this.querySelector('.hs-cart__suggestions');
      const carousel = section?.querySelector('.hs-cart__carousel');
      if (!carousel) return;
      section.hidden = !carousel.querySelector('.hs-cart__suggestion');
      const maximum = Math.max(0, carousel.scrollWidth - carousel.clientWidth);
      section.querySelector('[data-hs-slide="-1"]').disabled = carousel.scrollLeft <= 1;
      section.querySelector('[data-hs-slide="1"]').disabled = carousel.scrollLeft >= maximum - 1;
    }

    status(message = '', kind = 'error') {
      const status = this.querySelector('.hs-cart__status');
      status.textContent = message;
      status.dataset.kind = kind;
      status.hidden = !message;
    }

    onClick(event) {
      if (!(event.target instanceof Element)) return;
      const close = event.target.closest('[data-hs-close]');
      if (close && this.contains(close)) {
        event.preventDefault();
        return this.close();
      }
      const cartLink = event.target.closest('a[href], [data-open-cart-drawer], [data-cart-drawer-open]');
      if (cartLink && !this.contains(cartLink) && !event.ctrlKey && !event.metaKey && !event.shiftKey && event.button === 0) {
        let isCart = cartLink.matches('[data-open-cart-drawer], [data-cart-drawer-open]');
        if (cartLink.hasAttribute('href')) {
          const url = new URL(cartLink.getAttribute('href'), location.origin);
          isCart = url.origin === location.origin && /\/cart\/?$/.test(url.pathname);
        }
        if (isCart) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return this.open();
        }
      }
      const control = event.target.closest('[data-hs-step], [data-hs-remove], [data-hs-slide], [data-hs-remove-code]');
      if (!control || !this.contains(control)) return;
      event.preventDefault();
      if (control.hasAttribute('data-hs-slide')) {
        const carousel = this.querySelector('.hs-cart__carousel');
        const card = carousel.querySelector('.hs-cart__suggestion');
        if (!card) return;
        const gap = parseFloat(getComputedStyle(carousel).columnGap) || 0;
        carousel.scrollBy({ left: (card.getBoundingClientRect().width + gap) * Number(control.dataset.hsSlide), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
        return;
      }
      if (this.busy) return;
      if (control.hasAttribute('data-hs-remove-code')) return this.applyDiscount(this.codes.filter(code => code !== control.dataset.hsRemoveCode).join(','));
      const line = control.closest('[data-hs-line]');
      const quantity = control.hasAttribute('data-hs-remove') ? 0 : Math.max(0, Number(line.querySelector('[data-hs-quantity]').value) + Number(control.dataset.hsStep));
      this.change(line.dataset.hsLine, quantity);
    }

    onSubmit(event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.matches('[data-hs-discount]') && this.contains(form)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const code = new FormData(form).get('discount').trim();
        if (code && !this.busy) this.applyDiscount([...new Set([...this.codes, code])].join(','), code);
        return;
      }
      const url = new URL(form.action, location.origin);
      const customProductForm = form.matches('#product-form') && form.querySelector('#variant-id');
      if (url.origin !== location.origin || (!/\/cart\/add(\.js)?\/?$/.test(url.pathname) && !customProductForm)) return;
      if (!form.querySelector('[name="id"], [name^="items["]')) return;
      if (event.submitter?.name === 'checkout') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.pendingForms.has(form)) return;
      this.pendingForms.add(form);
      const data = new FormData(form);
      const buttons = [...form.querySelectorAll('button[type="submit"], input[type="submit"], button:not([type])')].filter(button => !button.disabled);
      buttons.forEach(button => { button.disabled = true; });
      this.run(async () => {
        this.open(false);
        await this.request('add', data);
      }).finally(() => {
        this.pendingForms.delete(form);
        buttons.forEach(button => { button.disabled = false; });
      });
    }

    async request(action, body) {
      const multipart = body instanceof FormData;
      const response = await fetch(`${this.root}cart/${action}.js`, {
        method: 'POST', credentials: 'same-origin',
        headers: multipart ? { Accept: 'application/json' } : { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: multipart ? body : JSON.stringify(body)
      });
      const data = await response.json();
      if (!response.ok || data.status >= 400) throw new Error(data.description || data.message || this.strings.error);
      return data;
    }

    run(action) {
      const operation = this.queue.then(async () => {
        this.busy = true;
        this.dialog.setAttribute('aria-busy', 'true');
        this.status(this.strings.loading, 'success');
        this.setControlsBusy(true);
        let message = '';
        try {
          await action();
        } catch (error) {
          message = error.message || this.strings.error;
        } finally {
          this.busy = false;
          this.lastContent = null;
          await this.refresh();
          this.dialog.removeAttribute('aria-busy');
          this.setControlsBusy(false);
          this.status(message, 'error');
          this.scheduleRefresh();
        }
      });
      this.queue = operation.catch(() => {});
      return operation;
    }

    setControlsBusy(busy) {
      this.querySelectorAll('[data-hs-step], [data-hs-remove], [data-hs-quantity], [data-hs-discount] button, .hs-cart__checkout').forEach(control => {
        control.disabled = busy || (control.matches('.hs-cart__checkout') && this.state?.count === 0);
      });
    }

    change(key, quantity) {
      if (this.busy) return;
      return this.run(() => this.request('change', { id: key, quantity }));
    }

    applyDiscount(codes, addedCode = '') {
      return this.run(async () => {
        const cart = await this.request('update', { discount: codes });
        if (addedCode && !cart.discount_codes?.some(discount => discount.code.toLowerCase() === addedCode.toLowerCase() && discount.applicable)) {
          throw new Error(this.strings.invalid_discount);
        }
      });
    }

    scheduleRefresh() {
      this.timers.forEach(clearTimeout);
      this.timers = [200, 900, 2200, 4500].map(delay => setTimeout(() => this.refresh(), delay));
    }

    refresh() {
      if (!this.isConnected || this.busy) return Promise.resolve();
      if (this.refreshPromise) {
        this.refreshAgain = true;
        return this.refreshPromise;
      }
      const version = this.mutationVersion;
      const connection = this.abortController;
      this.refreshPromise = (async () => {
        try {
          const sectionUrl = new URL(this.root, location.origin);
          sectionUrl.searchParams.set('section_id', this.dataset.sectionId);
          const [htmlResponse, cartResponse] = await Promise.all([
            fetch(sectionUrl, { credentials: 'same-origin', cache: 'no-store' }),
            fetch(`${this.root}cart.js`, { credentials: 'same-origin', cache: 'no-store' })
          ]);
          if (!htmlResponse.ok || !cartResponse.ok) throw new Error(this.strings.error);
          const [html, cart] = await Promise.all([htmlResponse.text(), cartResponse.json()]);
          if (!this.isConnected || connection !== this.abortController) return;
          if (version !== this.mutationVersion || this.busy) { this.refreshAgain = true; return; }
          let next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-hs-content]');
          if (!next || !next.querySelector('[data-hs-state]')) throw new Error(this.strings.error);
          if (this.designMode) next = this.previewContent(next, cart);
          const content = this.querySelector('[data-hs-content]');
          const signature = next.innerHTML;
          const codes = (cart.discount_codes || []).filter(code => code.applicable).map(code => code.code);
          if (signature === this.lastContent && JSON.stringify(codes) === JSON.stringify(this.codes)) return;
          this.lastContent = signature;
          const scroll = content.querySelector('.hs-cart__scroll').scrollTop;
          const carouselScroll = content.querySelector('.hs-cart__carousel')?.scrollLeft || 0;
          const discount = content.querySelector('[name="discount"]')?.value || '';
          const focus = document.activeElement;
          const focusId = this.contains(focus) ? focus.id : '';
          const lineKey = focus.closest?.('[data-hs-line]')?.dataset.hsLine;
          const focusAttribute = ['data-hs-quantity', 'data-hs-step', 'data-hs-remove'].find(attribute => focus.hasAttribute?.(attribute));
          const focusValue = focusAttribute ? focus.getAttribute(focusAttribute) : '';
          const suggestionChoices = new Map([...content.querySelectorAll('[data-hs-suggestion-variant]')].map(select => [select.id, select.value]));
          const reachedRewards = new Set([...content.querySelectorAll('[data-hs-reward].is-reached')].map(reward => reward.dataset.hsReward));
          next.querySelectorAll('[data-hs-reward].is-reached').forEach(reward => {
            if (!reachedRewards.has(reward.dataset.hsReward)) reward.classList.add('is-newly-reached');
          });
          content.replaceChildren(...next.childNodes);
          content.querySelectorAll('[data-hs-suggestion-variant]').forEach(select => {
            const choice = suggestionChoices.get(select.id);
            if (choice && [...select.options].some(option => option.value === choice)) {
              select.value = choice;
              this.updateSuggestionPrice(select);
            }
          });
          content.querySelector('.hs-cart__scroll').scrollTop = scroll;
          if (content.querySelector('.hs-cart__carousel')) content.querySelector('.hs-cart__carousel').scrollLeft = carouselScroll;
          this.updateCarouselControls();
          const discountInput = content.querySelector('[name="discount"]');
          if (discountInput) discountInput.value = discount;
          this.codes = codes;
          this.renderCodes();
          this.syncCounts();
          if (focusId) this.querySelector(`#${CSS.escape(focusId)}`)?.focus({ preventScroll: true });
          else if (lineKey && focusAttribute) this.querySelector(`[data-hs-line="${CSS.escape(lineKey)}"] [${focusAttribute}="${CSS.escape(focusValue)}"]`)?.focus({ preventScroll: true });
          if (this.dialog.open && !this.dialog.contains(document.activeElement)) this.querySelector('[data-hs-close]').focus({ preventScroll: true });
        } catch (error) {
          if (this.dialog.open) this.status(error.message || this.strings.error);
        } finally {
          this.refreshPromise = null;
          if (this.refreshAgain) { this.refreshAgain = false; this.refresh(); }
        }
      })();
      return this.refreshPromise;
    }

    previewContent(server, cart) {
      const next = this.editorBaseline.cloneNode(true);
      const items = next.querySelector('.hs-cart__items');
      const serverItems = server.querySelector('.hs-cart__items') || server.querySelector('[data-hs-preview-items]')?.content.querySelector('.hs-cart__items');
      if (items && serverItems) {
        const empty = (items.querySelector('.hs-cart__empty') || items.querySelector('[data-hs-preview-empty]')?.content.querySelector('.hs-cart__empty'))?.cloneNode(true);
        items.replaceChildren(...[...serverItems.childNodes].map(node => node.cloneNode(true)));
        if (empty && cart.item_count === 0) items.replaceChildren(empty);
      }
      const suggestions = next.querySelector('.hs-cart__suggestions');
      if (suggestions) {
        const source = suggestions.querySelector('[data-hs-preview-suggestions]')?.content || suggestions.querySelector('.hs-cart__carousel');
        const productIds = new Set(cart.items.map(item => String(item.product_id)));
        const cards = [...source.querySelectorAll('.hs-cart__suggestion')].filter(card => !productIds.has(card.dataset.hsProductId)).map(card => card.cloneNode(true));
        suggestions.querySelector('.hs-cart__carousel').replaceChildren(...cards);
      }
      const rewards = next.querySelector('[data-hs-reward-config]');
      if (rewards) {
        const config = JSON.parse(rewards.dataset.hsRewardConfig);
        const money = amount => new Intl.NumberFormat(document.documentElement.lang || 'fr', { style: 'currency', currency: cart.currency }).format(amount / 100).replace(/\s/g, '');
        const pending = config.thresholds.findIndex(threshold => cart.total_price < threshold);
        rewards.querySelector('.hs-cart__reward-message').textContent = pending < 0 ? config.all : config.templates[pending].replaceAll('[montant]', money(config.thresholds[pending] - cart.total_price));
        rewards.querySelectorAll('[data-hs-reward]').forEach((reward, index) => {
          const threshold = config.thresholds[index];
          const previous = config.thresholds[index - 1] || 0;
          const reached = cart.total_price >= threshold;
          reward.style.setProperty('--hs-progress', `${Math.max(0, Math.min(100, (cart.total_price - previous) * 100 / Math.max(1, threshold - previous)))}%`);
          reward.classList.toggle('is-reached', reached);
          reward.querySelector('.hs-cart__reward-check').hidden = !reached;
        });
        const hasGift = cart.items.some(item => item.final_line_price === 0 && item.handle?.includes('100g'));
        const hasSamples = cart.items.some(item => item.final_line_price === 0 && item.handle?.includes('echantillon'));
        rewards.querySelector('[data-hs-gift-success]').hidden = !hasGift;
        rewards.querySelector('[data-hs-samples-success]').hidden = hasGift || !hasSamples;
      }
      const footer = next.querySelector('.hs-cart__footer');
      footer.querySelector('.hs-cart__total strong').textContent = server.querySelector('.hs-cart__total strong').textContent;
      footer.querySelector('.hs-cart__checkout').disabled = cart.item_count === 0;
      footer.querySelectorAll('.hs-cart__discount-total').forEach(line => line.remove());
      server.querySelectorAll('.hs-cart__discount-total').forEach(line => footer.insertBefore(line.cloneNode(true), footer.querySelector('.hs-cart__total')));
      next.querySelector('[data-hs-state]').textContent = JSON.stringify({ count: cart.item_count, total: cart.total_price });
      return next;
    }

    renderCodes() {
      const target = this.querySelector('[data-hs-codes]');
      if (!target) return;
      target.replaceChildren();
      this.codes.forEach(code => {
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.hsRemoveCode = code;
        button.textContent = `${code} ×`;
        button.setAttribute('aria-label', `${this.strings.remove_discount}: ${code}`);
        target.append(button);
      });
    }

    syncCounts() {
      this.state = JSON.parse(this.querySelector('[data-hs-state]').textContent);
      this.querySelector('[data-hs-count]').textContent = this.state.count;
      document.querySelectorAll('.m-cart-count, .m-header__cart-count, [data-cart-count]').forEach(counter => {
        counter.textContent = this.state.count;
        counter.classList.toggle('m:hidden', this.state.count === 0);
        counter.classList.toggle('hidden', this.state.count === 0);
      });
    }
  }
  customElements.define('hs-cart-drawer', HSCartDrawer);
})();
